import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

const ConfirmCtx = createContext(null);

/**
 * useConfirm() returns a function:
 *   const confirm = useConfirm();
 *   if (await confirm({ title, message, confirmLabel, cancelLabel, variant })) { ... }
 * variant: "danger" (default) | "primary"
 */
export const useConfirm = () => {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmDialogProvider />");
  return ctx;
};

export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setState({
      title: opts.title || "¿Confirmar acción?",
      message: opts.message || "",
      confirmLabel: opts.confirmLabel || "Confirmar",
      cancelLabel: opts.cancelLabel || "Cancelar",
      variant: opts.variant || "danger",
    });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = (result) => {
    setState(null);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      if (e.key === "Enter") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state]);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <div
          data-testid="confirm-dialog"
          className="fixed inset-0 z-[100] bg-ink-900/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => close(false)}
        >
          <div
            className="bg-ink-800 border border-ink-500 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-ink-500 flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div
                  className={`shrink-0 w-9 h-9 flex items-center justify-center border ${
                    state.variant === "danger"
                      ? "bg-[#9B1C1C]/15 border-[#9B1C1C]/40 text-[#FF7A7A]"
                      : "bg-brand/15 border-brand/40 text-brand"
                  }`}
                >
                  <AlertTriangle size={18} strokeWidth={2} />
                </div>
                <div>
                  <h2 className="font-display font-bold text-base text-ash-100">{state.title}</h2>
                  {state.message && (
                    <p className="text-sm text-ash-300 mt-2 leading-relaxed">{state.message}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => close(false)}
                className="text-ash-500 hover:text-ash-100 transition-colors"
                data-testid="confirm-dialog-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-4 flex items-center justify-end gap-2">
              <button
                onClick={() => close(false)}
                data-testid="confirm-dialog-cancel"
                className="px-4 py-2.5 text-xs uppercase tracking-widest border border-ink-500 text-ash-100 hover:border-brand hover:text-brand transition-colors"
              >
                {state.cancelLabel}
              </button>
              <button
                onClick={() => close(true)}
                data-testid="confirm-dialog-confirm"
                autoFocus
                className={`px-5 py-2.5 text-xs uppercase tracking-widest font-semibold transition-colors ${
                  state.variant === "danger"
                    ? "bg-[#9B1C1C] hover:bg-[#7e1717] text-white"
                    : "bg-brand hover:bg-brand-hover text-ink-800"
                }`}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}
